/** API responses may use camelCase or PascalCase field names. */
export function pick(obj, ...keys) {
  if (obj == null || typeof obj !== "object") return undefined;
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] !== undefined && obj[k] !== null) {
      return obj[k];
    }
  }
  return undefined;
}

export function getAdminProfile(user) {
  return user?.profile && typeof user.profile === "object" ? user.profile : null;
}

export function formatAdminUserDobDisplay(user) {
  const p = getAdminProfile(user);
  const raw =
    pick(p, "dob", "DOB") ??
    (user?.patientMeta && pick(user.patientMeta, "dob", "DOB")) ??
    user?.metadata?.sourcePatientSnapshot?.dob;
  if (raw == null || raw === "") return "—";
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return String(raw);
  }
}

export function formatAdminUserDisplayName(user) {
  const p = getAdminProfile(user);
  if (!p) return "";
  const dn = String(pick(p, "displayName", "DisplayName") ?? "").trim();
  if (dn) return dn;
  const fn = String(pick(p, "firstName", "FirstName") ?? "").trim();
  const ln = String(pick(p, "lastName", "LastName") ?? "").trim();
  if (fn || ln) return [fn, ln].filter(Boolean).join(" ");
  return "";
}

export function formatAdminUserSex(user) {
  const s = pick(getAdminProfile(user), "sex", "Sex");
  return s != null && s !== "" ? String(s) : "";
}

export function contactPhoneList(contacts) {
  if (!contacts || typeof contacts !== "object") return [];
  const raw = contacts.phones ?? contacts.Phones;
  return Array.isArray(raw) ? raw : [];
}

export function contactEmailList(contacts) {
  if (!contacts || typeof contacts !== "object") return [];
  const raw = contacts.emails ?? contacts.Emails;
  return Array.isArray(raw) ? raw : [];
}

export function primaryPhoneDisplay(contacts) {
  const first = contactPhoneList(contacts)[0];
  if (!first) return "—";
  const n = pick(first, "e164", "E164");
  return n != null && n !== "" ? String(n) : "—";
}

/** Primary E.164 phone from admin `user.contacts`, or empty string when none. */
export function primaryPhoneLineOrEmpty(user) {
  if (!user) return "";
  const p = primaryPhoneDisplay(user.contacts);
  return p && p !== "—" ? p : "";
}

export function primaryEmailDisplay(contacts) {
  const first = contactEmailList(contacts)[0];
  if (!first) return "—";
  const em = pick(first, "email", "Email");
  return em != null && em !== "" ? String(em) : "—";
}

export function patientMetaField(pm, ...keys) {
  if (!pm || typeof pm !== "object") return "";
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(pm, k) && pm[k] != null && pm[k] !== "") {
      return pm[k];
    }
  }
  return "";
}

/** Server-set marker written by plato_sync's PatientProcessor when it owns a patient record. */
export const PLATO_MIGRATION_SOURCE = "plato.patients";

/**
 * A patient is "synced" when Plato owns its identity fields (only tags + notes stay editable).
 * Plato-owned records must also never be consumed by a merge: plato_sync would recreate the
 * record on its next pass, undoing the merge and re-splitting the conversation.
 */
export function isPlatoSynced(user) {
  return String(user?.metadata?.migrationSource || "").trim() === PLATO_MIGRATION_SOURCE;
}
